import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Text, Button, Input, Checkbox } from '../components/atoms';
import { BetaAccessNotice } from '../components/auth/BetaAccessNotice/BetaAccessNotice';
import { AuthLayout } from '../components/templates/AuthLayout';
import { Trans } from 'react-i18next';
import { useTranslation } from '../i18n/useTranslation';
import { LEGAL_PATHS } from '../components/legal/LegalLinks';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../lib/apiClient';
import { PRIVACY_POLICY_VERSION, TERMS_OF_SERVICE_VERSION } from '../config/legal';
import './RegisterPage.css';
import { Alert } from '../components/atoms/Alert/Alert';
import { FieldError } from '../components/atoms/FieldError/FieldError';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { getEmailErrorKey, getNameErrorKey, getNewPasswordErrorKey } from '../lib/authValidation';
import { uiLocale } from '../services/emailPreferencesService';
import { ACCOUNT_PENDING_PATH, markAccountApproval } from '../lib/accountApproval';

interface RegisterValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  consent: boolean;
  /** Optional, unticked by default: product updates / release notes by e-mail (double opt-in). */
  productUpdates: boolean;
  /** Optional, unticked by default: e-mail reminders for due tasks. */
  taskReminders: boolean;
}

interface RegisterErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  consent?: string;
}

/** Returns i18n keys per invalid field. */
function validate(values: RegisterValues): RegisterErrors {
  const errors: RegisterErrors = {};
  // Rules mirror the backend SignUpBodySchema (src/validators/authFields.ts).
  const firstNameError = getNameErrorKey(values.firstName, 'auth.firstNameRequired');
  if (firstNameError) errors.firstName = firstNameError;
  const lastNameError = getNameErrorKey(values.lastName, 'auth.lastNameRequired');
  if (lastNameError) errors.lastName = lastNameError;
  const emailError = getEmailErrorKey(values.email);
  if (emailError) errors.email = emailError;
  const passwordError = getNewPasswordErrorKey(values.password);
  if (passwordError) errors.password = passwordError;
  if (!values.confirmPassword) {
    errors.confirmPassword = 'auth.confirmPasswordRequired';
  // eslint-disable-next-line security/detect-possible-timing-attacks -- UI form validation; result surfaced immediately, no cryptographic risk
  } else if (values.password !== values.confirmPassword) {
    errors.confirmPassword = 'auth.passwordsDoNotMatch';
  }
  if (!values.consent) errors.consent = 'auth.consentRequired';
  return errors;
}

/** Maps a sign-up failure to an i18n key — never surfaces raw backend text. */
function signUpErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'USER_EXISTS' || err.status === 409) return 'auth.errorUserExists';
    if (err.code === 'RATE_LIMITED' || err.status === 429) return 'auth.errorRateLimited';
    if (err.code === 'INVALID_PASSWORD') return 'auth.errorInvalidPassword';
    if (err.code === 'VALIDATION_ERROR') return 'auth.errorInvalidInput';
    return 'auth.registrationFailed';
  }
  // fetch() rejects with a TypeError when the network/API is unreachable.
  if (err instanceof TypeError) return 'auth.errorNetwork';
  return 'auth.registrationFailed';
}

/** Maps an OTP verification failure to an i18n key. */
function verifyErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'CODE_MISMATCH') return 'auth.errorCodeMismatch';
    if (err.code === 'CODE_EXPIRED') return 'auth.errorCodeExpired';
    if (err.code === 'RATE_LIMITED' || err.status === 429) return 'auth.errorRateLimited';
    return 'auth.verificationFailed';
  }
  if (err instanceof TypeError) return 'auth.errorNetwork';
  return 'auth.verificationFailed';
}

/** Link inside the consent label; <Trans> supplies the translated text as children. */
function NewTabLink({ href, children }: { href: string; children?: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="register-page__link">
      {children}
    </a>
  );
}

const INITIAL_VALUES: RegisterValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  consent: false,
  productUpdates: false,
  taskReminders: false,
};

export function RegisterPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register, verifyOtp, resendOtp } = useAuth();

  const [values, setValues] = useState<RegisterValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<RegisterErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'register' | 'verify'>('register');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<string | null>(null);

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const consentRef = useRef<HTMLInputElement>(null);

  function updateField(field: 'firstName' | 'lastName' | 'email' | 'password' | 'confirmPassword') {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = e.target;
      setValues((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
  }

  function handleConsentChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const { checked } = e.target;
    setValues((prev) => ({ ...prev, consent: checked }));
    setErrors((prev) => ({ ...prev, consent: undefined }));
  }

  function handleEmailOptionChange(field: 'productUpdates' | 'taskReminders') {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { checked } = e.target;
      setValues((prev) => ({ ...prev, [field]: checked }));
    };
  }

  function focusFirstInvalid(found: RegisterErrors): void {
    const order: [string | undefined, React.RefObject<HTMLInputElement | null>][] = [
      [found.firstName, firstNameRef],
      [found.lastName, lastNameRef],
      [found.email, emailRef],
      [found.password, passwordRef],
      [found.confirmPassword, confirmPasswordRef],
      [found.consent, consentRef],
    ];
    const first = order.find(([message]) => message !== undefined);
    first?.[1].current?.focus();
  }

  async function handleRegister(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);

    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstInvalid(found);
      return;
    }

    setIsLoading(true);
    try {
      await register({
        email: values.email.trim(),
        password: values.password,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        privacyPolicyVersion: PRIVACY_POLICY_VERSION,
        tosVersion: TERMS_OF_SERVICE_VERSION,
        productUpdates: values.productUpdates,
        taskReminders: values.taskReminders,
        locale: uiLocale(i18n.language),
      });
      setStep('verify');
    } catch (err: unknown) {
      setFormError(t(signUpErrorKey(err)));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setVerifyError(null);
    setResendStatus(null);
    if (!otp.trim()) {
      setOtpError(t('auth.otpRequired'));
      return;
    }
    setOtpError(null);
    setIsVerifying(true);
    try {
      const { approvalStatus } = await verifyOtp(values.email.trim(), otp.trim());
      if (approvalStatus === 'approved') {
        navigate('/login');
      } else {
        // Closed beta: the e-mail address is confirmed, but an admin still has
        // to approve the account — explain that instead of offering the sign-in.
        markAccountApproval(approvalStatus);
        navigate(ACCOUNT_PENDING_PATH);
      }
    } catch (err: unknown) {
      setVerifyError(t(verifyErrorKey(err)));
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleResend(): Promise<void> {
    setVerifyError(null);
    setResendStatus(null);
    setIsResending(true);
    try {
      await resendOtp(values.email.trim());
      setResendStatus(t('auth.codeResent'));
    } catch (err: unknown) {
      setVerifyError(t(verifyErrorKey(err)));
    } finally {
      setIsResending(false);
    }
  }

  const fieldError = (key: string | undefined): string | undefined =>
    key !== undefined ? t(key) : undefined;

  if (step === 'verify') {
    return (
      <AuthLayout
        title={t('auth.verifyEmail')}
        subtitle={t('auth.verifyEmailDescription')}
      >
        <form onSubmit={handleVerify} className="register-page__form" noValidate>
          <Input
            id="otp"
            type="text"
            inputMode="numeric"
            label={t('auth.otpCode')}
            value={otp}
            onChange={(e) => {
              setOtp(e.target.value);
              setOtpError(null);
            }}
            required
            autoComplete="one-time-code"
            error={otpError ?? undefined}
          />
          {verifyError != null && (
            <Alert>{verifyError}</Alert>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={isVerifying}
            className="register-page__submit"
          >
            {isVerifying ? t('auth.verifying') : t('auth.verify')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => { void handleResend(); }}
            disabled={isResending || isVerifying}
          >
            {t('auth.resendCode')}
          </Button>
          <LiveRegion className="register-page__status">{resendStatus ?? ''}</LiveRegion>
        </form>
        <Text className="register-page__footer">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to="/login" className="register-page__link">
            {t('auth.signIn')}
          </Link>
        </Text>
      </AuthLayout>
    );
  }

  const consentErrorId = 'consent-error';
  const emailOptionsHelpId = 'email-options-help';
  const updatesHelpId = 'product-updates-help';

  return (
    <AuthLayout
      title={t('auth.createAccount')}
      subtitle={t('auth.registerSubheading')}
    >
      <form onSubmit={handleRegister} className="register-page__form" noValidate>
        <BetaAccessNotice />
        <div className="register-page__name-row">
          <Input
            ref={firstNameRef}
            id="firstName"
            type="text"
            label={t('auth.firstName')}
            value={values.firstName}
            onChange={updateField('firstName')}
            required
            autoComplete="given-name"
            maxLength={100}
            error={fieldError(errors.firstName)}
          />
          <Input
            ref={lastNameRef}
            id="lastName"
            type="text"
            label={t('auth.lastName')}
            value={values.lastName}
            onChange={updateField('lastName')}
            required
            autoComplete="family-name"
            maxLength={100}
            error={fieldError(errors.lastName)}
          />
        </div>
        <Input
          ref={emailRef}
          id="email"
          type="email"
          label={t('auth.email')}
          value={values.email}
          onChange={updateField('email')}
          required
          autoComplete="email"
          error={fieldError(errors.email)}
        />
        <Input
          ref={passwordRef}
          id="password"
          type="password"
          label={t('auth.password')}
          value={values.password}
          onChange={updateField('password')}
          required
          autoComplete="new-password"
          helperText={t('auth.passwordRules')}
          error={fieldError(errors.password)}
        />
        <Input
          ref={confirmPasswordRef}
          id="confirmPassword"
          type="password"
          label={t('auth.confirmPassword')}
          value={values.confirmPassword}
          onChange={updateField('confirmPassword')}
          required
          autoComplete="new-password"
          error={fieldError(errors.confirmPassword)}
        />
        <div className="register-page__consent">
          <Checkbox
            ref={consentRef}
            id="consent"
            label={
              // Links open in a new tab so the half-filled form is not lost.
              <Trans
                i18nKey="auth.consentLabel"
                components={{
                  terms: <NewTabLink href={LEGAL_PATHS.terms} />,
                  privacy: <NewTabLink href={LEGAL_PATHS.privacy} />,
                }}
              />
            }
            checked={values.consent}
            onChange={handleConsentChange}
            required
            aria-required="true"
            aria-invalid={errors.consent !== undefined ? 'true' : 'false'}
            aria-describedby={errors.consent !== undefined ? consentErrorId : undefined}
          />
          {errors.consent !== undefined && (
            <FieldError id={consentErrorId}>{t(errors.consent)}</FieldError>
          )}
        </div>
        {/* Optional e-mail consents — independent of the required consent above and unticked by
            default: a consent needs an active choice (GDPR recital 32, CJEU C-673/17 "Planet49",
            § 7 (2) no. 2 UWG). Product updates get their own highlighted box so they are not missed. */}
        <fieldset className="register-page__updates" aria-describedby={updatesHelpId}>
          <legend className="register-page__updates-legend">{t('auth.emailOptions.updatesLegend')}</legend>
          <Text id={updatesHelpId} size="sm">
            {t('auth.emailOptions.updatesDescription')}
          </Text>
          <Checkbox
            id="productUpdates"
            label={t('auth.emailOptions.productUpdates')}
            checked={values.productUpdates}
            onChange={handleEmailOptionChange('productUpdates')}
          />
        </fieldset>
        <fieldset className="register-page__email-options" aria-describedby={emailOptionsHelpId}>
          <legend className="register-page__email-legend">{t('auth.emailOptions.legend')}</legend>
          <Checkbox
            id="taskReminders"
            label={t('auth.emailOptions.taskReminders')}
            checked={values.taskReminders}
            onChange={handleEmailOptionChange('taskReminders')}
          />
          <Text id={emailOptionsHelpId} size="sm" color="muted">
            {t('auth.emailOptions.help')}
          </Text>
        </fieldset>
        {formError != null && (
          <Alert>{formError}</Alert>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading || !values.consent}
          className="register-page__submit"
        >
          {isLoading ? t('auth.registering') : t('auth.register')}
        </Button>
      </form>
      <Text className="register-page__footer">
        {t('auth.alreadyHaveAccount')}{' '}
        <Link to="/login" className="register-page__link">
          {t('auth.signIn')}
        </Link>
      </Text>
    </AuthLayout>
  );
}

export default RegisterPage;
